import { useState } from 'react'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import { ShortcutSection, ShortcutCategory, ShortcutSubcategory, getColorClasses } from '../../config/shortcuts'

interface ShortcutSectionModalProps {
  section: ShortcutSection
  onSelect: (category: ShortcutCategory, subcategory: ShortcutSubcategory) => void
  onClose: () => void
}

export default function ShortcutSectionModal({ section, onSelect, onClose }: ShortcutSectionModalProps) {
  const [selectedCategory, setSelectedCategory] = useState<ShortcutCategory | null>(null)

  const handleCategoryClick = (category: ShortcutCategory) => {
    setSelectedCategory(category)
  }

  const handleSubcategoryClick = (subcategory: ShortcutSubcategory) => {
    if (selectedCategory) {
      onSelect(selectedCategory, subcategory)
    }
  }

  const handleBack = () => {
    setSelectedCategory(null)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      {/* Backdrop click to close */}
      <div className="fixed inset-0" onClick={onClose} />

      {/* Modal content */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-200 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {selectedCategory && (
              <button
                onClick={handleBack}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
            )}
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {selectedCategory ? selectedCategory.label : section.label}
              </h2>
              {!selectedCategory && (
                <p className="text-sm text-gray-500">Wybierz kategorie</p>
              )}
              {selectedCategory && (
                <p className="text-sm text-gray-500">Wybierz rodzaj dokumentu</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {!selectedCategory ? (
            // Categories list
            <div className="space-y-2">
              {section.categories.map((category) => {
                const colors = getColorClasses(category.color)
                const Icon = category.icon

                return (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryClick(category)}
                    className="w-full flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 ${colors.bg} rounded-lg`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-left">
                        <h3 className="font-medium text-gray-900 group-hover:text-gray-700">
                          {category.label}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {category.subcategories.length} opcji
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
                  </button>
                )
              })}
            </div>
          ) : (
            // Subcategories list
            <div className="space-y-2">
              {selectedCategory.subcategories.map((subcategory) => (
                <button
                  key={subcategory.id}
                  onClick={() => handleSubcategoryClick(subcategory)}
                  className="w-full text-left p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all group"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-gray-400 group-hover:bg-blue-500 mt-2 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-900 group-hover:text-blue-700">
                        {subcategory.label}
                      </h4>
                      {subcategory.promptTemplate && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                          {subcategory.promptTemplate.length > 100
                            ? subcategory.promptTemplate.substring(0, 100) + '...'
                            : subcategory.promptTemplate}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4">
          <button
            onClick={selectedCategory ? handleBack : onClose}
            className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {selectedCategory ? 'Wstecz' : 'Anuluj'}
          </button>
        </div>
      </div>
    </div>
  )
}
